import React from 'react';
import { configureStore } from '@reduxjs/toolkit';
import type { Store } from '@reduxjs/toolkit';
import { Provider } from 'react-redux';
import rootReducer from './root-reducer';
import { routerMiddleware } from 'connected-react-router';
import { getHistory } from 'tradera/utils/history';
import { sentryReduxEnhancer } from 'tradera/utils/sentry/redux';
import { initializeState } from './state-initializer';
import initData from 'tradera/legacy/static/packages/init-data';
import { isServer } from 'tradera/utils/nextjs';
import { discoverApi } from './services/discover-api';
import { feesApi } from './services/fees-api';
import { touchwebSearchApi } from './services/touchweb-search-api';
import { touchwebSyiApi } from './services/touchweb-syi-api';
import { cartApi } from './services/cart-api';
import { messagingStatsApi } from './services/messaging-stats-api';
import type { History } from 'history';
import { bidHistoryApi } from 'tradera/components/bid-history/bid-history-api';
import { draftsApi } from 'tradera/apps/syi/state/drafts-api';
import { templatesApi } from 'tradera/apps/syi/state/templates-api';
import { shippingComplaintApi } from './services/shipping-complaint-api';
import { savedSearchesApi } from 'tradera/apps/my-tradera/saved-searches/saved-searches-api';
import { pickListApi } from 'tradera/apps/my-tradera/pick-list2/services/pick-list-api';
import { referralModalApi } from 'tradera/components/referral-modal/services/referral-modal-api';
import { wishListApi } from './services/wish-list-api';
import { wishListItemsEndingTodayApi } from './services/wish-list-items-ending-today-api';
import { ajaxItemApi } from 'tradera/components/quick-view/ajax-item-api';
import { firebaseApi } from 'tradera/components/messaging/firebase-api';
import { webApi } from 'tradera/state/services/webapi-api';
import { topListsApi } from 'tradera/apps/tradera/components/top-lists/top-lists-api';
import { sellerListApi } from 'tradera/apps/my-tradera/app/components/seller-list/seller-list-api';
import { bookedShipmentsApi } from 'tradera/apps/my-tradera/app/containers/booked-shipment-api';

if (isServer) {
    // Do not remove this without also removing the disable of eslint better-mutation/no-mutation at top of file.
    throw new Error(
        'This implementation is not compatible with SSR as global properties are shared between server instances'
    );
}

// Hot reloading
const enableWebPackHotModuleReplacementForReducers = (
    // webpack modules are not typed
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    module: any,
    store: Store<RootState>
) => {
    if (!module.hot) {
        return;
    }
    module.hot.accept('./root-reducer', () => {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const nextRootReducer = require('./root-reducer');
        store.replaceReducer(nextRootReducer);
    });
};

const initializeStore = (browserHistory: History) => {
    const isProd =
        typeof process === 'object' && process.env?.NODE_ENV === 'production';

    const reduxStore = configureStore({
        reducer: rootReducer(browserHistory),
        middleware: (getDefaultMiddleware) => {
            const middlewares = [
                discoverApi.middleware,
                feesApi.middleware,
                pickListApi.middleware,
                touchwebSearchApi.middleware,
                touchwebSyiApi.middleware,
                bidHistoryApi.middleware,
                cartApi.middleware,
                draftsApi.middleware,
                templatesApi.middleware,
                shippingComplaintApi.middleware,
                savedSearchesApi.middleware,
                referralModalApi.middleware,
                wishListApi.middleware,
                messagingStatsApi.middleware,
                firebaseApi.middleware,
                wishListItemsEndingTodayApi.middleware,
                ajaxItemApi.middleware,
                webApi.middleware,
                topListsApi.middleware,
                sellerListApi.middleware,
                bookedShipmentsApi.middleware
            ];
            if (browserHistory) {
                middlewares.push(routerMiddleware(browserHistory));
            }
            return getDefaultMiddleware().concat(middlewares);
        },
        // If url contains ?debug-redux devtools will be enabled in production;
        devTools: !isProd || window.location.search.includes('debug-redux'),
        enhancers: [sentryReduxEnhancer]
    });

    enableWebPackHotModuleReplacementForReducers(module, reduxStore);
    return reduxStore;
};

type SelectFn = (state: ReturnType<typeof store.getState>) => RootState;
type OnChangeFn = (previousState: RootState, currentState: RootState) => void;

// make it possible to observe state changes. used by syi
export const observeStore = function (select: SelectFn, onChange: OnChangeFn) {
    let currentState: RootState;
    function handleChange() {
        const nextState = select(store.getState());
        if (nextState !== currentState) {
            const previousState = currentState;
            // eslint-disable-next-line better-mutation/no-mutation
            currentState = nextState;
            onChange(previousState, currentState);
        }
    }

    const unsubscribe = store.subscribe(handleChange);
    handleChange();
    return unsubscribe;
};

const store = initializeStore(getHistory());

initializeState(initData)(store.dispatch);

export default store;

// Ref: https://redux-toolkit.js.org/tutorials/typescript
// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
export type GetState = () => RootState;

// Don't know how to type this properly...
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ProviderWithGlobalStore = (props: any) => (
    <Provider store={store} {...props} />
);
